import React, { useState } from 'react';
import { App, Button, Card, Col, Descriptions, Dropdown, Modal, Row, Select, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, EditOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import ReasonPrompt from './shared/ReasonPrompt';
import { fmtDate, fmtDateTime, fmtMoney, parseJsonArray, recruitErrorMessage, stageLabel } from './shared/recruitUi';

const { Text } = Typography;

const CandidateDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeInto, setMergeInto] = useState<string>();

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.candidate(id || ''),
    queryFn: () => hrmsService.getCandidate(id!),
    enabled: Boolean(id),
  });
  const { data: mergeOptions } = useQuery({
    queryKey: hrmsKeys.candidates({ q: mergeSearch, merge: 1 }),
    queryFn: () => hrmsService.pageCandidates({ page: 1, limit: 20, q: mergeSearch || undefined }),
    enabled: mergeOpen,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.candidate(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.candidates() });
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Candidate" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Candidate" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const clearBlacklist = () =>
    modal.confirm({
      title: 'Remove from blacklist?',
      onOk: async () => {
        try {
          await hrmsService.blacklistCandidate(id!, { blacklisted: false });
          message.success('Blacklist cleared');
          await invalidate();
        } catch (e: any) {
          message.error(recruitErrorMessage(e));
        }
      },
    });

  const tags = parseJsonArray<string>(row.tags_json);

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Candidate"
          documentNumber={row.candidate_number}
          subtitle={
            <Text type="secondary">
              {row.full_name}
              {row.current_title ? ` · ${row.current_title}` : ''}
            </Text>
          }
          statusTag={row.is_blacklisted ? <Tag color="red">Blacklisted</Tag> : <Tag color="green">Active</Tag>}
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/candidates')}>
                Back
              </Button>
              <Button icon={<EditOutlined />} onClick={() => navigate(`/hr/candidates/${id}/edit`)}>
                Edit
              </Button>
              {!row.is_blacklisted && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/hr/applications/new?candidate_id=${id}`)}>
                  Add to job
                </Button>
              )}
              <Dropdown
                menu={{
                  items: [
                    { key: 'merge', label: 'Merge duplicate into another record' },
                    row.is_blacklisted
                      ? { key: 'unblacklist', label: 'Remove from blacklist' }
                      : { key: 'blacklist', label: 'Blacklist', danger: true },
                  ],
                  onClick: ({ key }) => {
                    if (key === 'merge') setMergeOpen(true);
                    if (key === 'blacklist') setBlacklistOpen(true);
                    if (key === 'unblacklist') clearBlacklist();
                  },
                }}
              >
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            </Space>
          }
        />
      }
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Tabs
            items={[
              {
                key: 'applications',
                label: `Applications (${row.applications?.length || 0})`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={row.applications || []}
                    columns={[
                      { title: 'Application', dataIndex: 'application_number', render: (v, a: any) => <a onClick={() => navigate(`/hr/applications/${a.id}`)}>{v}</a> },
                      { title: 'Job', dataIndex: 'job_title', render: (v, a: any) => <a onClick={() => navigate(`/hr/jobs/${a.job_id}`)}>{v}</a> },
                      { title: 'Applied', dataIndex: 'created_at', width: 120, render: (v) => fmtDate(v) },
                      { title: 'Stage', dataIndex: 'status', width: 120, render: (s) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> },
                    ]}
                  />
                ),
              },
              {
                key: 'interviews',
                label: `Interviews (${row.interviews?.length || 0})`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={row.interviews || []}
                    columns={[
                      { title: 'When', dataIndex: 'scheduled_at', render: (v, i: any) => <a onClick={() => navigate(`/hr/interviews/${i.id}`)}>{fmtDateTime(v)}</a> },
                      { title: 'Round', dataIndex: 'round_number', width: 70 },
                      { title: 'Application', dataIndex: 'application_number', width: 140 },
                      { title: 'Score', dataIndex: 'overall_score', width: 80, render: (v) => (v != null ? `${Number(v).toFixed(1)}/5` : '—') },
                      { title: 'Recommendation', dataIndex: 'recommendation', width: 130 },
                      { title: 'Status', dataIndex: 'status', width: 110, render: (s) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> },
                    ]}
                  />
                ),
              },
              {
                key: 'assessments',
                label: `Assessments (${row.assessments?.length || 0})`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={row.assessments || []}
                    columns={[
                      { title: 'Assessment', dataIndex: 'assessment_number', render: (v, a: any) => <a onClick={() => navigate(`/hr/assessments/${a.id}`)}>{v}</a> },
                      { title: 'Title', dataIndex: 'title' },
                      { title: 'Score', dataIndex: 'percentage_score', width: 90, render: (v) => (v != null ? `${v}%` : '—') },
                      { title: 'Result', dataIndex: 'pass_fail', width: 90, render: (v) => (v ? <Tag color={v === 'Pass' ? 'green' : 'red'}>{v}</Tag> : '—') },
                      { title: 'Status', dataIndex: 'status', width: 110 },
                    ]}
                  />
                ),
              },
              {
                key: 'offers',
                label: `Offers (${row.offers?.length || 0})`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={row.offers || []}
                    columns={[
                      { title: 'Offer', dataIndex: 'offer_number', render: (v, o: any) => <a onClick={() => navigate(`/hr/offers/${o.id}`)}>{v}</a> },
                      { title: 'Role', dataIndex: 'job_title' },
                      { title: 'Basic', key: 'b', width: 140, render: (_, o: any) => fmtMoney(o.basic_salary, o.currency) },
                      { title: 'Join', dataIndex: 'join_date', width: 120, render: (v) => fmtDate(v) },
                      { title: 'Status', dataIndex: 'status', width: 120, render: (s) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> },
                    ]}
                  />
                ),
              },
              {
                key: 'comms',
                label: `Emails (${row.communications?.length || 0})`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={{ pageSize: 10 }}
                    dataSource={row.communications || []}
                    expandable={{
                      expandedRowRender: (c: any) => (
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {String(c.body || '—')
                            .replace(/<br\s*\/?>|<\/p>/gi, '\n')
                            .replace(/<[^>]+>/g, '')
                            .trim()}
                        </div>
                      ),
                    }}
                    columns={[
                      { title: 'Sent', dataIndex: 'created_at', width: 160, render: (v) => fmtDateTime(v) },
                      { title: 'Subject', dataIndex: 'subject', ellipsis: true },
                      { title: 'Template', dataIndex: 'template_key', width: 170 },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        width: 100,
                        render: (s, c: any) => (
                          <Tag color={s === 'Sent' ? 'green' : s === 'Failed' ? 'red' : 'default'} title={c.failed_reason || undefined}>
                            {s}
                          </Tag>
                        ),
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title="Profile" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Email">{row.email || '—'}</Descriptions.Item>
              <Descriptions.Item label="Phone">{row.phone || '—'}</Descriptions.Item>
              <Descriptions.Item label="Nationality">{row.nationality || '—'}</Descriptions.Item>
              <Descriptions.Item label="Location">{row.current_location || '—'}</Descriptions.Item>
              <Descriptions.Item label="Employer">{row.current_employer || '—'}</Descriptions.Item>
              <Descriptions.Item label="Experience">{row.experience_years != null ? `${row.experience_years} yr` : '—'}</Descriptions.Item>
              <Descriptions.Item label="Skills">{row.skills || '—'}</Descriptions.Item>
              <Descriptions.Item label="Source">
                {row.source}
                {row.agency_name ? ` — ${row.agency_name}` : ''}
                {row.referral_employee_name ? ` — referred by ${row.referral_employee_name}` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Tags">
                <Space size={[4, 4]} wrap>
                  {tags.length ? tags.map((t) => <Tag key={t}>{t}</Tag>) : '—'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Consent">{row.consent_at ? fmtDateTime(row.consent_at) : '—'}</Descriptions.Item>
              {row.prior_employee_id && (
                <Descriptions.Item label="Former employee">
                  <Tag color="orange">Re-hire</Tag>
                </Descriptions.Item>
              )}
              {row.is_blacklisted && <Descriptions.Item label="Blacklist reason">{row.blacklist_reason || '—'}</Descriptions.Item>}
              <Descriptions.Item label="Notes">
                <span style={{ whiteSpace: 'pre-wrap' }}>{row.notes || '—'}</span>
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <EntityAttachmentsCard
            entityType="HR_CAND"
            entityId={row.id}
            title="Candidate documents"
            uploadHint="CV, passport, certificates, referral letters"
          />
        </Col>
      </Row>

      <ReasonPrompt
        open={blacklistOpen}
        title="Blacklist candidate"
        okText="Blacklist"
        notesRequired
        notesLabel="Reason"
        onCancel={() => setBlacklistOpen(false)}
        onSubmit={async (v) => {
          try {
            await hrmsService.blacklistCandidate(id!, { blacklisted: true, reason: v.notes });
            message.success('Candidate blacklisted');
            setBlacklistOpen(false);
            await invalidate();
          } catch (e: any) {
            message.error(recruitErrorMessage(e));
          }
        }}
      />

      <Modal
        open={mergeOpen}
        title="Merge this candidate into another record"
        okText="Merge"
        okButtonProps={{ danger: true, disabled: !mergeInto }}
        onCancel={() => setMergeOpen(false)}
        onOk={async () => {
          try {
            await hrmsService.mergeCandidate(id!, mergeInto!);
            message.success('Records merged');
            setMergeOpen(false);
            await queryClient.invalidateQueries({ queryKey: hrmsKeys.candidates() });
            navigate(`/hr/candidates/${mergeInto}`, { replace: true });
          } catch (e: any) {
            message.error(recruitErrorMessage(e));
          }
        }}
      >
        <Text type="secondary">
          Applications, interviews, assessments, offers, emails and documents move to the selected record. This record ({row.candidate_number}) is then retired.
        </Text>
        <Select
          showSearch
          filterOption={false}
          onSearch={setMergeSearch}
          style={{ width: '100%', marginTop: 12 }}
          placeholder="Search the record to keep"
          value={mergeInto}
          onChange={setMergeInto}
          options={(mergeOptions?.rows || [])
            .filter((c: any) => c.id !== id)
            .map((c: any) => ({ value: c.id, label: `${c.candidate_number} — ${c.full_name}${c.email ? ` (${c.email})` : ''}` }))}
        />
      </Modal>
    </WorkspaceLayout>
  );
};

export default CandidateDetailPage;
